import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Download, FileSpreadsheet,
  Loader2, Upload, XCircle,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const CONFIG = {
  customers: { back: "/customers", preview: "/party-imports/customers/preview", template: "/party-imports/customers/template" },
  suppliers: { back: "/suppliers", preview: "/party-imports/suppliers/preview", template: "/party-imports/suppliers/template" },
  stock: { back: "/inventory", preview: "/stock-imports/preview", template: "/stock-imports/template" },
};

export default function MasterDataImport() {
  const { kind } = useParams();
  const config = CONFIG[kind] || CONFIG.customers;
  const { t } = useI18n();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [strategy, setStrategy] = useState("skip");
  const [batch, setBatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const counts = batch?.summary?.counts;
  const commitPath = useMemo(() => {
    if (!batch) return "";
    return kind === "stock"
      ? `/stock-imports/${batch.id}/commit`
      : `/party-imports/${kind}/${batch.id}/commit`;
  }, [batch, kind]);

  const preview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (kind !== "stock") form.append("strategy", strategy);
      const { data } = await api.post(config.preview, form);
      setBatch(data);
      toast.success(t("data_import.preview_ready"));
    } catch (error) {
      toast.error(apiErrorMessage(error, t));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(commitPath);
      setBatch(data);
      toast.success(t("data_import.committed"));
    } catch (error) {
      toast.error(apiErrorMessage(error, t));
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.get(config.template, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = kind === "customers" ? "kunden-import-vorlage.csv"
        : kind === "suppliers" ? "lieferanten-import-vorlage.csv"
          : "lagerbestand-import-vorlage.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(apiErrorMessage(error, t));
    }
  };

  const committed = batch?.status === "committed";
  return (
    <div>
      <PageHeader
        title={t(`data_import.${kind}_title`)}
        subtitle={t("data_import.subtitle")}
        actions={
          <Button variant="outline" onClick={() => navigate(config.back)}>
            <ArrowLeft className="h-4 w-4" />{t("common.back")}
          </Button>
        }
      />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-5">
          <Card className="p-5">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h2 className="font-heading text-lg font-semibold">{t("data_import.choose_file")}</h2>
                <p className="mt-1 text-sm text-slate-500">{t("data_import.file_help")}</p>
              </div>
              <Button variant="outline" size="sm" onClick={downloadTemplate}>
                <Download className="h-4 w-4" />{t("data_import.template")}
              </Button>
            </div>

            <Label
              htmlFor="master-data-file"
              className="flex min-h-40 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-6 text-center transition hover:border-primary/50 hover:bg-primary/5 dark:border-slate-700 dark:bg-slate-900/40"
            >
              <FileSpreadsheet className="mb-3 h-10 w-10 text-primary" />
              <span className="font-medium">{file?.name || t("data_import.drop_file")}</span>
              <span className="mt-1 text-xs font-normal text-slate-500">{t("data_import.formats")}</span>
              <input
                id="master-data-file" type="file" accept=".csv,.xlsx,text/csv"
                className="sr-only"
                onChange={(event) => { setFile(event.target.files?.[0] || null); setBatch(null); }}
                data-testid="master-data-import-file"
              />
            </Label>

            {kind !== "stock" && (
              <div className="mt-5">
                <Label>{t("data_import.existing_records")}</Label>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  {["skip", "update"].map((value) => (
                    <label key={value} className={`cursor-pointer rounded-lg border p-3 text-sm ${strategy === value ? "border-primary bg-primary/5" : ""}`}>
                      <input
                        type="radio" name="strategy" value={value}
                        checked={strategy === value}
                        onChange={() => { setStrategy(value); setBatch(null); }}
                        className="mr-2"
                      />
                      <span className="font-medium">{t(`data_import.${value}`)}</span>
                      <span className="mt-1 block pl-5 text-xs text-slate-500">{t(`data_import.${value}_help`)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-5 flex justify-end">
              <Button onClick={preview} disabled={!file || busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {t("data_import.check_file")}
              </Button>
            </div>
          </Card>

          {batch && (
            <Card className="overflow-hidden">
              <div className="border-b p-5">
                <h2 className="font-heading text-lg font-semibold">{t("data_import.preview")}</h2>
                <p className="mt-1 text-sm text-slate-500">{batch.filename}</p>
              </div>
              <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-4">
                <Stat label={t("data_import.total")} value={counts?.total || 0} />
                <Stat label={t("data_import.new_records")} value={counts?.create ?? counts?.adjustments ?? 0} color="text-emerald-600" />
                <Stat label={t("data_import.updates")} value={counts?.update ?? 0} color="text-blue-600" />
                <Stat label={t("data_import.errors")} value={counts?.errors || 0} color={counts?.errors ? "text-red-600" : ""} />
              </div>

              {batch.summary.errors?.length > 0 && (
                <div className="border-t p-5">
                  <div className="mb-3 flex items-center gap-2 font-medium text-red-600">
                    <XCircle className="h-4 w-4" />{t("data_import.rows_with_errors")}
                  </div>
                  <div className="max-h-64 overflow-auto rounded-lg border">
                    <Table>
                      <TableHeader><TableRow><TableHead>{t("data_import.row")}</TableHead><TableHead>{t("common.name")}</TableHead><TableHead>{t("data_import.problem")}</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {batch.summary.errors.map((error, index) => (
                          <TableRow key={`${error.row}-${index}`}>
                            <TableCell>{error.row}</TableCell>
                            <TableCell>{error.name || error.sku || error.ean || "—"}</TableCell>
                            <TableCell className="text-red-600">{error.codes.map((code) => t(`data_import.codes.${code}`)).join(", ")}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {batch.summary.warnings?.length > 0 && (
                <div className="border-t p-5 text-sm">
                  <div className="flex items-center gap-2 font-medium text-amber-600">
                    <AlertTriangle className="h-4 w-4" />
                    {t("data_import.warnings", { count: batch.summary.warnings.length })}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3 border-t bg-slate-50 p-5 dark:bg-slate-900/40 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate-500">
                  {committed ? t("data_import.commit_complete") : t("data_import.commit_help")}
                </p>
                {!committed && (
                  <Button onClick={commit} disabled={busy || !counts?.valid}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    {t("data_import.import_now")}
                  </Button>
                )}
              </div>
            </Card>
          )}
        </div>

        <Card className="h-fit p-5">
          <h2 className="font-heading font-semibold">{t("data_import.how_it_works")}</h2>
          <ol className="mt-4 space-y-4 text-sm">
            {[1, 2, 3].map((number) => (
              <li key={number} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">{number}</span>
                <span>{t(`data_import.step_${number}`)}</span>
              </li>
            ))}
          </ol>
          {kind === "stock" && (
            <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              {t("data_import.stock_notice")}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, value, color = "" }) {
  return (
    <div className="bg-background p-4 text-center">
      <div className={`text-2xl font-bold tabular-nums ${color}`}>{value}</div>
      <div className="mt-1 text-xs text-slate-500">{label}</div>
    </div>
  );
}
